// src/utils/statistics.ts — Pure statistical functions for pharmacology data analysis
import jStat from 'jstat';

// ============================================================
// DESCRIPTIVE STATISTICS
// ============================================================

export interface DescriptiveStats {
  n: number;
  mean: number;
  median: number;
  std: number;
  sem: number;
  q1: number;
  q3: number;
  iqr: number;
  min: number;
  max: number;
  skewness: number;
  kurtosis: number;
  whiskerLow: number;
  whiskerHigh: number;
  outliers: number[];
}

export function descriptiveStats(data: number[]): DescriptiveStats | null {
  const clean = data.filter(d => d != null && !isNaN(d)).sort((a, b) => a - b);
  const n = clean.length;
  if (n === 0) return null;

  const mean = jStat.mean(clean);
  const median = jStat.median(clean);
  const std = n > 1 ? jStat.stdev(clean, true) : 0;
  const sem = n > 1 ? std / Math.sqrt(n) : 0;
  const q1 = jStat.percentile(clean, 0.25);
  const q3 = jStat.percentile(clean, 0.75);
  const iqr = q3 - q1;
  const whiskerLow = Math.max(clean[0], q1 - 1.5 * iqr);
  const whiskerHigh = Math.min(clean[n - 1], q3 + 1.5 * iqr);
  const outliers = clean.filter(v => v < whiskerLow || v > whiskerHigh);

  return {
    n, mean, median, std, sem, q1, q3, iqr,
    min: clean[0], max: clean[n - 1],
    skewness: n > 2 ? jStat.skewness(clean) : 0,
    kurtosis: n > 3 ? jStat.kurtosis(clean) : 0,
    whiskerLow, whiskerHigh, outliers,
  };
}

// ============================================================
// NORMALITY CHECK (heuristic)
// ============================================================

export interface NormalityCheck {
  isApproxNormal: boolean;
  reason: string;
}

export function checkNormality(stats: DescriptiveStats): NormalityCheck {
  if (stats.n < 8) {
    return { isApproxNormal: false, reason: `n=${stats.n} too small to assess` };
  }
  const skewOk = Math.abs(stats.skewness) < 2;
  const kurtOk = Math.abs(stats.kurtosis) < 7;
  if (skewOk && kurtOk) {
    return { isApproxNormal: true, reason: 'Approximately normal' };
  }
  const parts: string[] = [];
  if (!skewOk) parts.push(`skewness=${stats.skewness.toFixed(2)}`);
  if (!kurtOk) parts.push(`kurtosis=${stats.kurtosis.toFixed(2)}`);
  return { isApproxNormal: false, reason: `Non-normal (${parts.join(', ')})` };
}

// ============================================================
// T-TEST (Welch's — 2 groups)
// ============================================================

export interface TTestResult {
  test: 'welch-t';
  t: number;
  df: number;
  pValue: number;
  significant: boolean;
  meanDiff: number;
  ci95: [number, number];
  effectSize: number; // Cohen's d
  group1Stats: DescriptiveStats;
  group2Stats: DescriptiveStats;
}

export function independentTTest(group1: number[], group2: number[]): TTestResult | null {
  const s1 = descriptiveStats(group1);
  const s2 = descriptiveStats(group2);
  if (!s1 || !s2 || s1.n < 2 || s2.n < 2) return null;

  const meanDiff = s1.mean - s2.mean;
  const se = Math.sqrt((s1.std ** 2 / s1.n) + (s2.std ** 2 / s2.n));
  if (se === 0) return null;

  const t = meanDiff / se;

  // Welch-Satterthwaite degrees of freedom
  const v1 = s1.std ** 2 / s1.n;
  const v2 = s2.std ** 2 / s2.n;
  const df = (v1 + v2) ** 2 / (v1 ** 2 / (s1.n - 1) + v2 ** 2 / (s2.n - 1));

  // Two-tailed p-value
  const pValue = 2 * (1 - jStat.studentt.cdf(Math.abs(t), df));

  // Cohen's d
  const pooledStd = Math.sqrt(((s1.n - 1) * s1.std ** 2 + (s2.n - 1) * s2.std ** 2) / (s1.n + s2.n - 2));
  const effectSize = pooledStd > 0 ? meanDiff / pooledStd : 0;

  // 95% CI
  const tCrit = jStat.studentt.inv(0.975, df);
  const ci95: [number, number] = [meanDiff - tCrit * se, meanDiff + tCrit * se];

  return {
    test: 'welch-t', t, df, pValue,
    significant: pValue < 0.05,
    meanDiff, ci95, effectSize,
    group1Stats: s1, group2Stats: s2,
  };
}

// ============================================================
// MANN-WHITNEY U (non-parametric 2-group)
// ============================================================

export interface WilcoxonResult {
  test: 'mann-whitney-u';
  U: number;
  z: number;
  pValue: number;
  significant: boolean;
  n1: number;
  n2: number;
}

export function mannWhitneyU(group1: number[], group2: number[]): WilcoxonResult | null {
  const g1 = group1.filter(d => !isNaN(d));
  const g2 = group2.filter(d => !isNaN(d));
  const n1 = g1.length;
  const n2 = g2.length;
  if (n1 < 2 || n2 < 2) return null;

  const all = [
    ...g1.map(v => ({ v, g: 1 })),
    ...g2.map(v => ({ v, g: 2 })),
  ].sort((a, b) => a.v - b.v);

  // Assign ranks with tie averaging
  const ranks = new Array(all.length);
  let i = 0;
  while (i < all.length) {
    let j = i;
    while (j < all.length && all[j].v === all[i].v) j++;
    const avgRank = (i + j + 1) / 2;
    for (let k = i; k < j; k++) ranks[k] = avgRank;
    i = j;
  }

  let R1 = 0;
  all.forEach((item, idx) => {
    if (item.g === 1) R1 += ranks[idx];
  });

  const U1 = R1 - n1 * (n1 + 1) / 2;
  const U2 = n1 * n2 - U1;
  const U = Math.min(U1, U2);

  const mU = n1 * n2 / 2;
  const sigmaU = Math.sqrt(n1 * n2 * (n1 + n2 + 1) / 12);
  const z = sigmaU > 0 ? (U - mU) / sigmaU : 0;
  const pValue = 2 * (1 - jStat.normal.cdf(Math.abs(z), 0, 1));

  return { test: 'mann-whitney-u', U, z, pValue, significant: pValue < 0.05, n1, n2 };
}

// ============================================================
// ONE-WAY ANOVA (3+ groups)
// ============================================================

export interface AnovaResult {
  test: 'one-way-anova';
  F: number;
  dfBetween: number;
  dfWithin: number;
  pValue: number;
  significant: boolean;
  etaSquared: number;
  groupStats: { name: string; stats: DescriptiveStats }[];
}

export function oneWayAnova(groups: { name: string; data: number[] }[]): AnovaResult | null {
  const validGroups = groups
    .map(g => ({ name: g.name, data: g.data.filter(d => !isNaN(d)) }))
    .filter(g => g.data.length >= 2);
  if (validGroups.length < 2) return null;

  const allData = validGroups.flatMap(g => g.data);
  const grandMean = jStat.mean(allData);
  const k = validGroups.length;
  const N = allData.length;

  let ssBetween = 0;
  let ssWithin = 0;
  const groupStats: { name: string; stats: DescriptiveStats }[] = [];

  for (const group of validGroups) {
    const stats = descriptiveStats(group.data);
    if (!stats) continue;
    groupStats.push({ name: group.name, stats });
    ssBetween += group.data.length * (stats.mean - grandMean) ** 2;
    ssWithin += group.data.reduce((sum, v) => sum + (v - stats.mean) ** 2, 0);
  }

  const dfBetween = k - 1;
  const dfWithin = N - k;
  if (dfWithin <= 0) return null;

  const msBetween = ssBetween / dfBetween;
  const msWithin = ssWithin / dfWithin;
  const F = msWithin > 0 ? msBetween / msWithin : 0;

  const pValue = 1 - jStat.centralF.cdf(F, dfBetween, dfWithin);
  const etaSquared = (ssBetween + ssWithin) > 0 ? ssBetween / (ssBetween + ssWithin) : 0;

  return {
    test: 'one-way-anova', F, dfBetween, dfWithin, pValue,
    significant: pValue < 0.05, etaSquared, groupStats,
  };
}

// ============================================================
// POST-HOC TESTS
// ============================================================

export interface PostHocPair {
  group1: string;
  group2: string;
  pValue: number;
  pAdjusted: number;
  significant: boolean;
  meanDiff: number;
}

export interface PostHocResult {
  method: string;
  pairs: PostHocPair[];
}

export function bonferroniCorrection(pValues: number[]): number[] {
  const n = pValues.length;
  return pValues.map(p => Math.min(1, p * n));
}

export function benjaminiHochberg(pValues: number[]): number[] {
  const n = pValues.length;
  const indexed = pValues.map((p, i) => ({ p, i })).sort((a, b) => a.p - b.p);
  const adjusted = new Array(n);
  let cumMin = 1;
  for (let k = n - 1; k >= 0; k--) {
    const adj = Math.min(1, indexed[k].p * n / (k + 1));
    cumMin = Math.min(cumMin, adj);
    adjusted[indexed[k].i] = cumMin;
  }
  return adjusted;
}

// Tukey's HSD (Bonferroni-approximated)
export function tukeyHSD(groups: { name: string; data: number[] }[]): PostHocResult {
  const validGroups = groups
    .map(g => ({ name: g.name, data: g.data.filter(d => !isNaN(d)) }))
    .filter(g => g.data.length >= 2);

  const N = validGroups.reduce((sum, g) => sum + g.data.length, 0);
  const k = validGroups.length;

  let ssWithin = 0;
  const stats = validGroups.map(g => {
    const s = descriptiveStats(g.data)!;
    ssWithin += g.data.reduce((sum, v) => sum + (v - s.mean) ** 2, 0);
    return { name: g.name, ...s };
  });
  const msWithin = ssWithin / (N - k);

  const pairs: PostHocPair[] = [];
  for (let i = 0; i < k; i++) {
    for (let j = i + 1; j < k; j++) {
      const si = stats[i], sj = stats[j];
      const se = Math.sqrt(msWithin * (1 / si.n + 1 / sj.n) / 2);
      const q = se > 0 ? Math.abs(si.mean - sj.mean) / se : 0;
      const t = q / Math.sqrt(2);
      const df = N - k;
      const rawP = 2 * (1 - jStat.studentt.cdf(Math.abs(t), df));
      const nComparisons = k * (k - 1) / 2;
      const pAdj = Math.min(1, rawP * nComparisons);

      pairs.push({
        group1: si.name, group2: sj.name,
        pValue: rawP, pAdjusted: pAdj,
        significant: pAdj < 0.05,
        meanDiff: si.mean - sj.mean,
      });
    }
  }
  return { method: "Tukey's HSD (Bonferroni-approximated)", pairs };
}

// Dunn's test (non-parametric post-hoc)
export function dunnsTest(groups: { name: string; data: number[] }[]): PostHocResult {
  const validGroups = groups
    .map(g => ({ name: g.name, data: g.data.filter(d => !isNaN(d)) }))
    .filter(g => g.data.length >= 2);

  const all: { v: number; g: number }[] = [];
  validGroups.forEach((g, gi) => {
    g.data.forEach(v => all.push({ v, g: gi }));
  });
  all.sort((a, b) => a.v - b.v);

  const N = all.length;
  const ranks = new Array(N);
  let i = 0;
  while (i < N) {
    let j = i;
    while (j < N && all[j].v === all[i].v) j++;
    const avgRank = (i + j + 1) / 2;
    for (let k = i; k < j; k++) ranks[k] = avgRank;
    i = j;
  }

  const groupRanks = validGroups.map(() => [] as number[]);
  all.forEach((item, idx) => groupRanks[item.g].push(ranks[idx]));
  const meanRanks = groupRanks.map(r => jStat.mean(r));
  const ns = groupRanks.map(r => r.length);

  const pairs: PostHocPair[] = [];
  const rawPs: number[] = [];

  for (let a = 0; a < validGroups.length; a++) {
    for (let b = a + 1; b < validGroups.length; b++) {
      const denom = Math.sqrt(N * (N + 1) / 12 * (1 / ns[a] + 1 / ns[b]));
      const z = denom > 0 ? (meanRanks[a] - meanRanks[b]) / denom : 0;
      const rawP = 2 * (1 - jStat.normal.cdf(Math.abs(z), 0, 1));
      rawPs.push(rawP);
      pairs.push({
        group1: validGroups[a].name, group2: validGroups[b].name,
        pValue: rawP, pAdjusted: rawP,
        significant: false, meanDiff: meanRanks[a] - meanRanks[b],
      });
    }
  }

  const adjusted = bonferroniCorrection(rawPs);
  pairs.forEach((p, idx) => {
    p.pAdjusted = adjusted[idx];
    p.significant = adjusted[idx] < 0.05;
  });

  return { method: "Dunn's test (Bonferroni-corrected)", pairs };
}

// ============================================================
// HELPERS
// ============================================================

export function significanceStars(p: number): string {
  if (p < 0.001) return '***';
  if (p < 0.01) return '**';
  if (p < 0.05) return '*';
  return 'ns';
}

export function effectSizeLabel(d: number): string {
  const abs = Math.abs(d);
  if (abs < 0.2) return 'negligible';
  if (abs < 0.5) return 'small';
  if (abs < 0.8) return 'medium';
  return 'large';
}

export function formatP(p: number): string {
  if (p < 0.001) return '< 0.001';
  if (p < 0.01) return p.toFixed(4);
  return p.toFixed(3);
}
